/**
 * DustyTrain HUD Color System
 * Neon noir cyberpunk palette
 */

export const COLORS = {
  // Primary neon colors
  cyan: '#05f3ff',
  neonGreen: '#3bff9f',
  violet: '#b05fff',
  amber: '#ffb84d',
  red: '#ff4d4d',
  electricBlue: '#4d9dff',
  
  // Category colors
  water: '#05f3ff',
  utilities: '#05f3ff',
  environmental: '#3bff9f',
  transportation: '#ffb84d',
  construction: '#ff8c4d',
  general: '#b05fff',
  highValue: '#ff4d4d',
  
  // Background
  background: '#0b0b13',
  backgroundLight: '#141423',
  
  // UI elements
  gridLine: 'rgba(5, 243, 255, 0.15)',
  text: '#ffffff',
  textDim: 'rgba(255, 255, 255, 0.6)',
  textDimmer: 'rgba(255, 255, 255, 0.4)',
  
  // Glass/panels
  panelBg: 'rgba(11, 11, 19, 0.85)',
  panelBorder: 'rgba(5, 243, 255, 0.3)',
  
  // Interactive states
  hover: '#00ffff',
  selected: '#ffffff',
  disabled: 'rgba(255, 255, 255, 0.3)'
};

/**
 * Get color by category
 */
export function getCategoryColor(category) {
  const normalized = category.toLowerCase().replace(/[^a-z]/g, '');
  
  const mapping = {
    water: COLORS.water,
    utilities: COLORS.utilities,
    wastewater: COLORS.water,
    environmental: COLORS.environmental,
    remediation: COLORS.environmental,
    transportation: COLORS.transportation,
    transit: COLORS.transportation,
    highway: COLORS.transportation,
    construction: COLORS.construction,
    building: COLORS.construction,
    general: COLORS.general,
    other: COLORS.general
  };
  
  return mapping[normalized] || COLORS.electricBlue;
}

/**
 * Get color by value (for high-value projects)
 */
export function getValueColor(value) {
  if (value > 5000000) return COLORS.red;
  if (value > 2000000) return COLORS.amber;
  return null; // Use category color
}

/**
 * Convert hex to RGB array
 */
export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255
  ] : [1, 1, 1];
}

/**
 * Get glow color with opacity
 */
export function getGlowColor(hex, intensity = 1.0) {
  const rgb = hexToRgb(hex);
  return `rgba(${rgb[0] * 255}, ${rgb[1] * 255}, ${rgb[2] * 255}, ${intensity})`;
}

/**
 * Interpolate between two colors
 */
export function interpolateColor(color1, color2, factor) {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  const result = rgb1.map((c1, i) => {
    const c2 = rgb2[i];
    return c1 + (c2 - c1) * factor;
  });
  
  return `rgb(${result[0] * 255}, ${result[1] * 255}, ${result[2] * 255})`;
}

/**
 * Get category display name
 */
export function getCategoryName(category) {
  const names = {
    water: 'Water & Utilities',
    utilities: 'Utilities',
    environmental: 'Environmental',
    transportation: 'Transportation',
    construction: 'Construction',
    general: 'General Infrastructure'
  };
  
  return names[category.toLowerCase()] || category;
}

/**
 * Format currency
 */
export function formatCurrency(amount) {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  } else {
    return `$${amount.toFixed(0)}`;
  }
}

/**
 * Format date
 */
export function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Get days until deadline
 */
export function getDaysUntil(deadline) {
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const diff = deadlineDate - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  return days;
}

/**
 * Get urgency color
 */
export function getUrgencyColor(deadline) {
  const days = getDaysUntil(deadline);
  
  if (days < 0) return COLORS.disabled;
  if (days <= 7) return COLORS.red;
  if (days <= 14) return COLORS.amber;
  if (days <= 30) return COLORS.neonGreen;
  return COLORS.cyan;
}